/**
 * MFE manifest registry (Faster Delivery).
 *
 * Decides once per page load which MFE versions run, from
 * `mfe-registry/v1/manifest.json` on the MFE CDN. Strict when enabled:
 * manifest -> bundled import, never the legacy window payload and never a
 * client-side cached manifest.
 */
import { getQueryParamsAndQueryString } from '@thoughtspot/env-flags';
import { isMfeManifestEnabled } from '@thoughtspot/feature-service';
import { create } from '@thoughtspot/logger';
import {
    collectEvent,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import {
    getMfeManifestUrl,
    MFE_ASSET_FILE_PATTERN,
    MFE_CDN_HOSTS,
    MFE_ENTRY_PATH_PATTERN,
    MfeCdnEnv,
    MfeManifest,
} from '@thoughtspot/microfrontends';
import { publishManifestToWindow } from './mfe-window-bridge';

export type MfeRegistrySource = 'network' | 'unavailable' | 'disabled';

export interface MfeRegistryState {
    /** LD flag on and a manifest URL resolved. */
    enabled: boolean;
    source: MfeRegistrySource;
    manifest: MfeManifest | null;
    /** Origin the manifest was fetched from; asset URLs resolve against it. */
    cdnOrigin?: string;
    etag?: string;
    /** Served from the index.html early fetch. */
    preloaded?: boolean;
}

const logger = create('mfe-registry');

const MANIFEST_FETCH_TIMEOUT_MS = 4000;
/** Dev/test substitute for the nginx-injected URL; allow-listed envs only. */
const MFE_CDN_ENV_QUERY_PARAM = 'mfeCdnEnv';
const CALVER = /^\d{4}\.\d{2}\.\d{2}-\d+$/;
/** App names are keys on a plain object; `__proto__` must never reach it. */
const MFE_NAME = /^[a-z][a-z0-9-]*$/;

function isAllowedManifestUrl(url: string): boolean {
    try {
        return Object.values(MFE_CDN_HOSTS).includes(new URL(url).origin);
    } catch (e) {
        return false;
    }
}

const isMfeCdnEnv = (value: unknown): value is MfeCdnEnv =>
    typeof value === 'string' && value in MFE_CDN_HOSTS;

const DISABLED_STATE: MfeRegistryState = Object.freeze({
    enabled: false,
    source: 'disabled',
    manifest: null,
});

let ready: Promise<MfeRegistryState> | null = null;

/**
 * Dual gate: the `enableMfeManifest` LD flag AND a cluster-injected
 * `blink.mfeManifestUrl` on an allow-listed CDN host. `?mfeCdnEnv=` is the
 * dev/test override.
 */
export function resolveManifestUrl(): string | null {
    if (!isMfeManifestEnabled()) {
        logger.info(
            'MFE manifest registry off (enableMfeManifest=false); using legacy MFE delivery',
        );
        return null;
    }
    const override = getQueryParamsAndQueryString().queryParams?.[
        MFE_CDN_ENV_QUERY_PARAM
    ];
    if (override !== undefined) {
        if (isMfeCdnEnv(override)) return getMfeManifestUrl(override);
        logger.warn(
            `Ignoring ?${MFE_CDN_ENV_QUERY_PARAM}=${override}; expected one of ${Object.keys(
                MFE_CDN_HOSTS,
            ).join(' | ')}`,
        );
    }
    const injected = (window as any).blink?.mfeManifestUrl;
    if (typeof injected === 'string' && injected) {
        if (isAllowedManifestUrl(injected)) return injected;
        logger.error(
            `Ignoring injected manifest URL ${injected}: not on an MFE CDN host`,
        );
        return null;
    }
    logger.info(
        'enableMfeManifest is on but this cluster has no manifest URL configured ' +
            '(tscli cluster mfe-manifest-url); using legacy MFE delivery. ' +
            `Add ?${MFE_CDN_ENV_QUERY_PARAM}=dev|staging|prod to test without it.`,
    );
    return null;
}

/**
 * Shape validation only; the format contract is the `/v1/` URL path. Unknown
 * fields are ignored so the publisher can add fields ahead of the shell.
 */
export function assertValidManifest(
    raw: unknown,
    manifestUrl: string,
): MfeManifest {
    const manifest = raw as MfeManifest;
    if (!raw || typeof raw !== 'object') {
        throw new Error(
            `manifest at ${manifestUrl}: expected a JSON object, received ${typeof raw}`,
        );
    }
    if (!manifest.mfes || typeof manifest.mfes !== 'object') {
        throw new Error(
            `manifest at ${manifestUrl}: missing "mfes" object, received keys [${Object.keys(
                raw,
            ).join(', ')}]`,
        );
    }
    const cdnOrigin = new URL(manifestUrl).origin;
    if (!Object.values(MFE_CDN_HOSTS).includes(cdnOrigin)) {
        throw new Error(`manifest: ${cdnOrigin} is not an MFE CDN host`);
    }
    Object.entries(manifest.mfes).forEach(([name, entry]) => {
        if (
            !MFE_NAME.test(name) ||
            !entry ||
            !CALVER.test(entry.version) ||
            !MFE_ENTRY_PATH_PATTERN.test(entry.entryPath) ||
            !MFE_ASSET_FILE_PATTERN.test(entry.js) ||
            (entry.css !== undefined && !MFE_ASSET_FILE_PATTERN.test(entry.css))
        ) {
            throw new Error(`manifest: invalid entry for ${name}`);
        }
    });
    return manifest;
}

/**
 * Consumes the index.html early fetch once, and only when it targets the URL
 * this registry resolved. It is a transport optimisation, not a decision input.
 */
function takeEarlyManifestFetch(url: string): Promise<Response> | null {
    const blink = (window as any).blink;
    if (!blink?.mfeManifestFetch || blink.mfeManifestUrl !== url) {
        return null;
    }
    const early = blink.mfeManifestFetch;
    blink.mfeManifestFetch = undefined;
    return early;
}

function withTimeout<T>(promise: Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(
            () =>
                reject(
                    new Error(`timed out after ${MANIFEST_FETCH_TIMEOUT_MS}ms`),
                ),
            MANIFEST_FETCH_TIMEOUT_MS,
        );
        promise
            .then(value => {
                clearTimeout(timer);
                resolve(value);
                return value;
            })
            .catch(error => {
                clearTimeout(timer);
                reject(error);
            });
    });
}

async function parseManifestResponse(
    response: Response,
    url: string,
): Promise<MfeManifest> {
    if (!response.ok) {
        throw new Error(
            `manifest at ${url}: HTTP ${response.status} ` +
                `(${
                    response.status === 403 || response.status === 404
                        ? 'not published to this CDN yet?'
                        : response.statusText
                })`,
        );
    }
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('json')) {
        // A non-JSON 200 is almost always a fallback page for a missing object.
        throw new Error(
            `manifest at ${url}: expected application/json, received "${contentType}"`,
        );
    }
    return assertValidManifest(await response.json(), url);
}

async function fetchManifest(url: string): Promise<MfeRegistryState> {
    const toState = (
        manifest: MfeManifest,
        response: Response,
        preloaded: boolean,
    ): MfeRegistryState => ({
        enabled: true,
        source: 'network',
        manifest,
        cdnOrigin: new URL(url).origin,
        etag: response.headers.get('etag') ?? undefined,
        preloaded,
    });

    const early = takeEarlyManifestFetch(url);
    if (early) {
        try {
            const response = await withTimeout(early);
            return toState(
                await parseManifestResponse(response, url),
                response,
                true,
            );
        } catch (error) {
            logger.warn(
                `early manifest fetch unusable (${error.message}); refetching`,
            );
        }
    }

    const controller = new AbortController();
    const timer = setTimeout(
        () => controller.abort(),
        MANIFEST_FETCH_TIMEOUT_MS,
    );
    try {
        // no-cache: revalidate every load. No cache-busting query strings: the
        // CDN keys its cache on them.
        const response = await fetch(url, {
            cache: 'no-cache',
            credentials: 'omit',
            signal: controller.signal,
        });
        return toState(
            await parseManifestResponse(response, url),
            response,
            false,
        );
    } finally {
        clearTimeout(timer);
    }
}

function manifestAgeMs(manifest: MfeManifest | null): number | undefined {
    const generatedAt = manifest && Date.parse(manifest.generatedAt);
    return Number.isFinite(generatedAt)
        ? Math.max(0, Date.now() - generatedAt)
        : undefined;
}

function reportLoaded(state: MfeRegistryState, startedAt: number): void {
    const mfeCount = state.manifest
        ? Object.keys(state.manifest.mfes).length
        : 0;
    const latencyMs = Math.round(performance.now() - startedAt);
    logger.info(
        `MFE manifest ${state.source}: ${mfeCount} MFEs from ${state.cdnOrigin} ` +
            `in ${latencyMs}ms`,
    );
    collectEvent(new UserAction(userActionTypeEnum.MFE_MANIFEST_LOADED), {
        source: state.source,
        latency_ms: latencyMs,
        manifest_age_ms: manifestAgeMs(state.manifest) ?? 'NA',
        etag: state.etag ?? 'NA',
        preloaded: state.preloaded ?? false,
        mfe_count: mfeCount,
    });
}

function decide(): Promise<MfeRegistryState> {
    const url = resolveManifestUrl();
    if (!url) {
        return Promise.resolve(DISABLED_STATE);
    }

    const cdnOrigin = new URL(url).origin;
    const startedAt = performance.now();
    logger.info(`Loading MFE manifest from ${url}`);
    return fetchManifest(url)
        .catch(
            (error): MfeRegistryState => {
                logger.error(
                    'MFE manifest unavailable; MFEs will load from the shell bundle',
                    error,
                );
                return {
                    enabled: true,
                    source: 'unavailable',
                    manifest: null,
                    cdnOrigin,
                };
            },
        )
        .then(state => {
            if (state.manifest) Object.freeze(state.manifest);
            // Publish before anyone can observe the registry as ready.
            publishManifestToWindow(state);
            reportLoaded(state, startedAt);
            return state;
        });
}

/**
 * The one decision for this page load: fetch the manifest and publish it to
 * `window.blink.microfrontends`, replacing the bootstrap's payload. Idempotent.
 *
 * Bootstrap must await this before the first MFE resolves a URL — see
 * `startMfePrefetch()`. Nothing else awaits the registry: resolution stays the
 * synchronous window read it has always been, so an MFE render path never
 * depends on this promise. Library mode never calls it and keeps the
 * bootstrap's legacy payload.
 */
export function initMfeRegistry(): Promise<MfeRegistryState> {
    if (!ready) {
        ready = decide();
    }
    return ready;
}

/** Test-only. */
export function resetMfeRegistryForTests(): void {
    ready = null;
}
